<script>
  // Invoicing for clients and other brands (not store orders). Drafts are edited freely; issuing gives an invoice its number and
  // locks the money and the parties; payments are recorded against it. The server recomputes every total (src/invoices.js);
  // the figures here are a live preview from the same arithmetic.
  import { onMount } from 'svelte';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Badge from '../ui/Badge.svelte';
  import Tabs from '../ui/Tabs.svelte';
  import { inputCls, textareaCls, selectCls, labelCls, hintCls, thCls, tdCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import { calc, balanceOf, displayStatus, addDays, todayLocal, fmtMoney } from '../../lib/invoiceMath.js';
  import { buildInvoicePdf } from '../../lib/invoicePdf.js';
  import Plus from 'lucide-svelte/icons/plus';
  import ArrowLeft from 'lucide-svelte/icons/arrow-left';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import Download from 'lucide-svelte/icons/download';
  import Copy from 'lucide-svelte/icons/copy';
  import Ban from 'lucide-svelte/icons/ban';
  import Banknote from 'lucide-svelte/icons/banknote';
  import Search from 'lucide-svelte/icons/search';
  import Receipt from 'lucide-svelte/icons/receipt';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';
  import TriangleAlert from 'lucide-svelte/icons/triangle-alert';

  let { site = {}, navigate = () => {} } = $props();
  const settings = $derived(site?.invoicing || {});
  const currency = $derived(site?.currency || 'R');
  const money = (n, c) => fmtMoney(n, c || currency);

  let invoices = $state([]);
  let loading = $state(true);
  let loadError = $state('');
  let filter = $state('all');
  let query = $state('');
  let editing = $state(null);          // the working copy while the editor is open
  let busy = $state('');               // which action is running: save | issue | send | pdf | pay | void | dup | delete
  let payOpen = $state(false);
  let pay = $state({ amount: '', date: todayLocal(), method: 'EFT', note: '' });

  const STATUS = {
    draft:   { label: 'Draft',            variant: 'secondary' },
    issued:  { label: 'Awaiting payment', variant: 'warning' },
    partial: { label: 'Part paid',        variant: 'info' },
    overdue: { label: 'Overdue',          variant: 'destructive' },
    paid:    { label: 'Paid',             variant: 'success' },
    void:    { label: 'Void',             variant: 'outline' },
  };

  async function api(method, url, body) {
    const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }
  async function load() {
    loading = true; loadError = '';
    try { invoices = await api('GET', '/api/invoices'); }
    catch (e) { loadError = e.message; }
    finally { loading = false; }
  }
  onMount(load);

  const upsert = (inv) => { invoices = invoices.some(i => i.id === inv.id) ? invoices.map(i => i.id === inv.id ? inv : i) : [inv, ...invoices]; };

  // ── List ───────────────────────────────────────────────────────────────────
  const today = $derived(todayLocal());
  const withStatus = $derived(invoices.map(i => ({ ...i, ds: displayStatus(i, today) })));
  const sum = (list, f) => list.reduce((a, i) => a + f(i), 0);
  const open = $derived(withStatus.filter(i => ['issued', 'partial', 'overdue'].includes(i.ds)));
  const stats = $derived({
    outstanding: sum(open, i => i.balance), overdue: sum(open.filter(i => i.ds === 'overdue'), i => i.balance),
    paidMonth: sum(withStatus.flatMap(i => i.payments || []).filter(p => String(p.date).slice(0, 7) === today.slice(0, 7)), p => p.amount),
    drafts: withStatus.filter(i => i.ds === 'draft').length, overdueCount: open.filter(i => i.ds === 'overdue').length,
  });
  const tabItems = $derived([
    { value: 'all', label: 'All', count: withStatus.length },
    { value: 'draft', label: 'Drafts', count: withStatus.filter(i => i.ds === 'draft').length },
    { value: 'open', label: 'Outstanding', count: open.length },
    { value: 'overdue', label: 'Overdue', count: stats.overdueCount },
    { value: 'paid', label: 'Paid', count: withStatus.filter(i => i.ds === 'paid').length },
    { value: 'void', label: 'Void', count: withStatus.filter(i => i.ds === 'void').length },
  ]);
  const shown = $derived(withStatus.filter(i => {
    if (filter === 'open' ? !['issued', 'partial', 'overdue'].includes(i.ds) : filter !== 'all' && i.ds !== filter) return false;
    const q = query.toLowerCase().trim();
    return !q || [i.number, i.client?.name, i.client?.company, i.client?.email, i.client?.reference].some(v => String(v || '').toLowerCase().includes(q));
  }));
  const whoOf = (i) => i.client?.company || i.client?.name || 'No client yet';
  // Past clients, for filling the form in with one pick.
  const knownClients = $derived([...new Map(invoices.filter(i => i.client?.company || i.client?.name).map(i => [(i.client.company || i.client.name).toLowerCase(), i.client])).values()]);

  // ── Editor ─────────────────────────────────────────────────────────────────
  const blankItem = () => ({ description: '', quantity: 1, unitPrice: 0 });
  function newInvoice() {
    const issueDate = todayLocal();
    openEditor({
      id: '', number: '', status: 'draft', client: { name: '', company: '', email: '', address: '', vatNumber: '', reference: '' },
      issueDate, dueDate: addDays(issueDate, Number(settings.defaultTerms ?? 14)), items: [blankItem()],
      discountType: 'none', discountValue: 0, taxRate: Number(settings.defaultTaxRate ?? 15), taxLabel: settings.taxLabel || 'VAT',
      notes: settings.defaultNotes || '', currency, payments: [],
    });
  }
  function openEditor(inv) {
    editing = JSON.parse(JSON.stringify(inv));
    editing.items = editing.items?.length ? editing.items : [blankItem()];
    payOpen = false;
    window.scrollTo?.(0, 0);
  }
  function closeEditor() { editing = null; }

  const locked = $derived(Boolean(editing && editing.status !== 'draft'));
  const t = $derived(editing ? calc(editing) : calc({}));
  const bal = $derived(editing ? balanceOf(t.total, editing.payments) : { paid: 0, balance: 0 });
  const ds = $derived(editing ? displayStatus({ ...editing, total: t.total }, today) : 'draft');
  const editable = $derived(editing && !['void', 'paid'].includes(editing.status));

  function pickClient(e) {
    const known = knownClients.find(c => (c.company || c.name).toLowerCase() === e.target.value.trim().toLowerCase());
    if (known && !locked) editing.client = { ...editing.client, ...known, reference: editing.client.reference };
  }
  function setTerms(days) { if (days !== '') editing.dueDate = addDays(editing.issueDate, Number(days)); }
  function setIssueDate(v) { const shift = editing.issueDate && editing.dueDate ? (Date.parse(editing.dueDate) - Date.parse(editing.issueDate)) / 86400000 : Number(settings.defaultTerms ?? 14); editing.issueDate = v; if (v) editing.dueDate = addDays(v, shift); }

  async function run(name, fn) {
    if (busy) return null;
    busy = name;
    try { return await fn(); }
    catch (e) { toast.error(e.message); return null; }
    finally { busy = ''; }
  }

  async function save(quiet = false) {
    const body = { ...editing };
    const inv = editing.id ? await api('PUT', `/api/invoices/${editing.id}`, body) : await api('POST', '/api/invoices', body);
    upsert(inv); openEditor(inv);
    if (!quiet) toast.success(editing.status === 'draft' ? 'Draft saved' : 'Changes saved');
    return inv;
  }
  const doSave = () => run('save', () => save());

  async function issue() {
    return run('issue', async () => {
      let inv = editing.id ? await save(true) : await save(true);
      if (inv.status === 'draft') { inv = await api('POST', `/api/invoices/${inv.id}/issue`); upsert(inv); openEditor(inv); toast.success(`Issued as ${inv.number}`); }
      return inv;
    });
  }
  // Business details and the logo: what the PDF is drawn from.
  const pdfFor = (inv) => buildInvoicePdf({ invoice: { ...inv, currency: inv.currency || currency }, business: settings, site });
  const downloadPdf = () => run('pdf', async () => {
    const { doc, filename } = await pdfFor(editing);
    doc.save(filename);
  });
  function startPayment() { pay = { amount: bal.balance.toFixed(2), date: todayLocal(), method: 'EFT', note: '' }; payOpen = true; }
  const recordPayment = () => run('pay', async () => {
    const inv = await api('POST', `/api/invoices/${editing.id}/payments`, { ...pay, amount: Number(pay.amount) });
    upsert(inv); openEditor(inv); payOpen = false;
    toast.success(inv.status === 'paid' ? 'Paid in full' : 'Payment recorded');
  });
  const removePayment = (i) => run('pay', async () => {
    if (!(await confirmDialog.ask({ title: 'Remove this payment?', description: 'The invoice goes back to awaiting payment if it was paid in full.', confirmLabel: 'Remove', destructive: true }))) return;
    const inv = await api('DELETE', `/api/invoices/${editing.id}/payments/${i}`);
    upsert(inv); openEditor(inv);
  });
  const voidInvoice = () => run('void', async () => {
    if (!(await confirmDialog.ask({ title: `Void ${editing.number}?`, description: 'A voided invoice keeps its number but is no longer owed, and cannot be paid or edited. To correct a mistake, void it and duplicate it into a new draft.', confirmLabel: 'Void invoice', destructive: true }))) return;
    const inv = await api('POST', `/api/invoices/${editing.id}/void`);
    upsert(inv); openEditor(inv);
    toast.success(`${inv.number} voided`);
  });
  const duplicate = (id = editing?.id) => run('dup', async () => {
    const inv = await api('POST', `/api/invoices/${id}/duplicate`);
    upsert(inv); openEditor(inv);
    toast.success('Copied into a new draft');
  });
  const removeDraft = () => run('delete', async () => {
    if (!(await confirmDialog.ask({ title: 'Delete this draft?', description: 'Drafts have no number yet, so nothing is lost from your records.', confirmLabel: 'Delete draft', destructive: true }))) return;
    if (editing.id) { await api('DELETE', `/api/invoices/${editing.id}`); invoices = invoices.filter(i => i.id !== editing.id); }
    closeEditor();
    toast.success('Draft deleted');
  });

  const setupMissing = $derived(!settings.businessName || !settings.accountNumber);
  const lineAmount = (i) => calc({ items: [i] }).total;
  const fmtDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
</script>

{#if !editing}
  <div class="space-y-6">
    <div class="flex items-end justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">Invoices</h1>
        <p class="text-sm text-muted-foreground">Invoice clients and other brands, send them as a PDF and track who has paid. Store orders are separate.</p>
      </div>
      <Button onclick={newInvoice}><Plus size={16} /> New invoice</Button>
    </div>

    {#if setupMissing}
      <div role="status" class="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
        <TriangleAlert size={16} class="mt-0.5 shrink-0" />
        <p>Add your business name and bank details first, so they appear on every invoice. <a href="/admin/settings" onclick={(e) => { e.preventDefault(); navigate('settings'); setTimeout(() => document.getElementById('settings-invoicing')?.scrollIntoView({ behavior: 'smooth' }), 400); }} class="font-medium underline underline-offset-2">Open Settings → Invoicing</a>.</p>
      </div>
    {/if}

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {#each [['Outstanding', money(stats.outstanding), `${open.length} open invoice${open.length === 1 ? '' : 's'}`, ''], ['Overdue', money(stats.overdue), `${stats.overdueCount} past due`, stats.overdueCount ? 'text-destructive' : ''], ['Received this month', money(stats.paidMonth), 'payments recorded', ''], ['Drafts', String(stats.drafts), 'not sent yet', '']] as [label, value, note, tone]}
        <Card><div class="p-4">
          <p class="text-sm text-muted-foreground">{label}</p>
          <p class="mt-1 text-2xl font-semibold tabular-nums tracking-tight {tone}">{value}</p>
          <p class="text-xs text-muted-foreground">{note}</p>
        </div></Card>
      {/each}
    </div>

    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="overflow-x-auto"><Tabs items={tabItems} bind:value={filter} label="Filter invoices" /></div>
      <div class="relative w-full lg:w-80">
        <Search size={15} class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input type="search" bind:value={query} placeholder="Search number, client or reference" aria-label="Search invoices" class="{inputCls} pl-9" />
      </div>
    </div>

    <Card class="overflow-hidden">
      {#if loading}
        <div class="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" role="status"><LoaderCircle size={16} class="animate-spin" /> Loading invoices…</div>
      {:else if loadError}
        <div class="flex flex-col items-center gap-3 py-16 text-center"><p class="text-sm text-destructive">{loadError}</p><Button variant="outline" onclick={load}>Try again</Button></div>
      {:else if shown.length === 0}
        <div class="flex flex-col items-center gap-2 py-16 text-center">
          <Receipt size={28} class="text-muted-foreground" />
          <p class="font-medium">{withStatus.length ? 'No invoices match' : 'No invoices yet'}</p>
          <p class="text-sm text-muted-foreground">{withStatus.length ? 'Try another filter or search.' : 'Create your first one to bill a client or brand.'}</p>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="w-full caption-bottom text-sm">
            <thead class="border-b border-border"><tr>
              <th class={thCls}>Invoice</th><th class={thCls}>Client</th><th class="{thCls} hidden md:table-cell">Due</th>
              <th class="{thCls} text-right">Amount</th><th class="{thCls} hidden sm:table-cell text-right">Balance</th><th class={thCls}>Status</th>
            </tr></thead>
            <tbody>
              {#each shown as inv (inv.id)}
                <tr class="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-muted/50" onclick={() => openEditor(inv)}>
                  <td class={tdCls}><button type="button" class="text-left font-medium tabular-nums focus-visible:underline focus-visible:outline-hidden">{inv.number || 'Draft'}</button><div class="text-xs text-muted-foreground">{fmtDate(inv.issueDate)}</div></td>
                  <td class={tdCls}><div class="font-medium">{whoOf(inv)}</div>{#if inv.client?.company && inv.client?.name}<div class="text-xs text-muted-foreground">{inv.client.name}</div>{/if}</td>
                  <td class="{tdCls} hidden md:table-cell {inv.ds === 'overdue' ? 'text-destructive' : 'text-muted-foreground'}">{fmtDate(inv.dueDate)}</td>
                  <td class="{tdCls} text-right font-medium tabular-nums">{money(inv.total, inv.currency)}</td>
                  <td class="{tdCls} hidden sm:table-cell text-right tabular-nums text-muted-foreground">{['draft', 'void'].includes(inv.ds) ? '—' : money(inv.balance, inv.currency)}</td>
                  <td class={tdCls}><Badge variant={STATUS[inv.ds].variant}>{STATUS[inv.ds].label}</Badge></td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  </div>
{:else}
  <div class="max-w-6xl space-y-6 pb-12">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <button type="button" onclick={closeEditor} class="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"><ArrowLeft size={14} /> Invoices</button>
        <h1 class="flex items-center gap-3 text-2xl font-semibold tracking-tight">{editing.number || (editing.id ? 'Draft invoice' : 'New invoice')} <Badge variant={STATUS[ds].variant}>{STATUS[ds].label}</Badge></h1>
        
      </div>
    </div>

    {#if locked}
      <p class="rounded-lg bg-muted p-3 text-sm text-muted-foreground">{editing.status === 'issued' ? 'This invoice has been issued, so the client, lines, discount and tax are locked. You can still change the due date and notes. To correct a mistake, void it and duplicate it into a new draft.' : editing.status === 'paid' ? 'This invoice is paid in full.' : 'This invoice is void.'}</p>
    {/if}

    <div class="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div class="space-y-6">
        <Card title="Bill to">
          <div class="space-y-4 p-6 pt-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label for="iv-company" class={labelCls}>Company or brand</label>
                <input id="iv-company" list="iv-clients" bind:value={editing.client.company} onchange={pickClient} disabled={locked} maxlength="120" class={inputCls} placeholder="e.g. Brand Co (Pty) Ltd" autocomplete="off" />
                <datalist id="iv-clients">{#each knownClients as c}<option value={c.company || c.name}></option>{/each}</datalist>
              </div>
              <div><label for="iv-name" class={labelCls}>Contact person</label><input id="iv-name" bind:value={editing.client.name} disabled={locked} maxlength="120" class={inputCls} placeholder="e.g. Ayanda Dlamini" /></div>
              <div><label for="iv-email" class={labelCls}>Email</label><input id="iv-email" type="email" bind:value={editing.client.email} disabled={locked} maxlength="254" class={inputCls} placeholder="accounts@brand.co" /></div>
              <div><label for="iv-ref" class={labelCls}>Their reference / PO number</label><input id="iv-ref" bind:value={editing.client.reference} disabled={locked} maxlength="80" class={inputCls} placeholder="Optional" /></div>
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_220px]">
              <div><label for="iv-addr" class={labelCls}>Billing address</label><textarea id="iv-addr" bind:value={editing.client.address} disabled={locked} maxlength="400" rows={3} class={textareaCls}></textarea></div>
              <div><label for="iv-vat" class={labelCls}>Their VAT number</label><input id="iv-vat" bind:value={editing.client.vatNumber} disabled={locked} maxlength="40" class={inputCls} placeholder="Optional" /></div>
            </div>
          </div>
        </Card>

        <Card title="Items" description="What you are charging for.">
          <div class="space-y-3 p-6 pt-4">
            <div class="hidden grid-cols-[minmax(0,1fr)_84px_120px_110px_36px] gap-2 px-1 text-xs font-medium text-muted-foreground sm:grid"><span>Description</span><span>Qty</span><span>Unit price</span><span class="text-right">Amount</span><span></span></div>
            {#each editing.items as item, i (i)}
              <div class="grid grid-cols-2 gap-2 rounded-lg border border-border p-3 sm:grid-cols-[minmax(0,1fr)_84px_120px_110px_36px] sm:items-start sm:border-0 sm:p-0">
                <div class="col-span-2 sm:col-span-1"><label class="sr-only" for="iv-d{i}">Description of line {i + 1}</label><textarea id="iv-d{i}" bind:value={item.description} disabled={locked} maxlength="300" rows={1} class="{textareaCls} min-h-9 py-2" placeholder="e.g. Campaign shoot, full day"></textarea></div>
                <div><label class="sr-only" for="iv-q{i}">Quantity</label><input id="iv-q{i}" type="number" min="0" step="any" inputmode="decimal" bind:value={item.quantity} disabled={locked} class="{inputCls} tabular-nums" /></div>
                <div><label class="sr-only" for="iv-p{i}">Unit price</label><input id="iv-p{i}" type="number" min="0" step="0.01" inputmode="decimal" bind:value={item.unitPrice} disabled={locked} class="{inputCls} tabular-nums" /></div>
                <div class="flex h-9 items-center justify-end text-sm font-medium tabular-nums">{money(lineAmount(item), editing.currency)}</div>
                <div class="flex justify-end"><Button variant="ghost" size="icon" class="h-9 w-9 hover:text-destructive" aria-label="Remove line {i + 1}" disabled={locked || editing.items.length === 1} onclick={() => (editing.items = editing.items.filter((_, n) => n !== i))}><Trash2 size={15} /></Button></div>
              </div>
            {/each}
            {#if !locked}<Button variant="outline" size="sm" onclick={() => (editing.items = [...editing.items, blankItem()])}><Plus size={14} /> Add a line</Button>{/if}
          </div>
        </Card>

        <Card title="Notes" description="Shown at the bottom of the invoice: payment terms, thanks, anything the client should know.">
          <div class="p-6 pt-4"><label class="sr-only" for="iv-notes">Notes</label><textarea id="iv-notes" bind:value={editing.notes} disabled={!editable} maxlength="2000" rows={3} class={textareaCls}></textarea></div>
        </Card>

        {#if editing.status !== 'draft'}
          <Card title="Payments" description={editing.status === 'void' ? 'This invoice is void.' : 'Record money as it arrives.'}>
            <div class="space-y-3 p-6 pt-4">
              {#each editing.payments || [] as p, i}
                <div class="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                  <div class="min-w-0"><p class="font-medium tabular-nums">{money(p.amount, editing.currency)} <span class="font-normal text-muted-foreground">· {p.method}</span></p><p class="truncate text-xs text-muted-foreground">{fmtDate(p.date)}{p.note ? ` · ${p.note}` : ''}</p></div>
                  {#if editing.status !== 'void'}<Button variant="ghost" size="icon" class="h-8 w-8 hover:text-destructive" aria-label="Remove payment {i + 1}" disabled={!!busy} onclick={() => removePayment(i)}><Trash2 size={14} /></Button>{/if}
                </div>
              {:else}
                <p class="text-sm text-muted-foreground">No payments recorded yet.</p>
              {/each}
              {#if editing.status === 'issued'}
                {#if payOpen}
                  <div class="space-y-3 rounded-lg border border-border p-4">
                    <div class="grid grid-cols-2 gap-3">
                      <div><label for="pay-amt" class={labelCls}>Amount received</label><input id="pay-amt" type="number" min="0" step="0.01" inputmode="decimal" bind:value={pay.amount} class="{inputCls} tabular-nums" /></div>
                      <div><label for="pay-date" class={labelCls}>Date</label><input id="pay-date" type="date" bind:value={pay.date} class={inputCls} /></div>
                      <div><label for="pay-method" class={labelCls}>How</label><select id="pay-method" bind:value={pay.method} class={selectCls}>{#each ['EFT', 'Cash', 'Card', 'Other'] as m}<option>{m}</option>{/each}</select></div>
                      <div><label for="pay-note" class={labelCls}>Note</label><input id="pay-note" bind:value={pay.note} maxlength="200" class={inputCls} placeholder="Optional" /></div>
                    </div>
                    <div class="flex gap-2"><Button size="sm" disabled={!!busy} onclick={recordPayment}>{#if busy === 'pay'}<LoaderCircle size={14} class="animate-spin" />{/if} Record payment</Button><Button size="sm" variant="ghost" onclick={() => (payOpen = false)}>Cancel</Button></div>
                  </div>
                {:else}
                  <Button variant="outline" size="sm" onclick={startPayment}><Banknote size={14} /> Record a payment</Button>
                {/if}
              {/if}
            </div>
          </Card>
        {/if}
      </div>

      <!-- Summary and actions -->
      <div class="space-y-6 lg:sticky lg:top-20">
        <Card title="Summary">
          <div class="space-y-4 p-6 pt-4">
            <div class="grid grid-cols-2 gap-3">
              <div><label for="iv-issue" class={labelCls}>Invoice date</label><input id="iv-issue" type="date" value={editing.issueDate} onchange={(e) => setIssueDate(e.target.value)} disabled={locked} class={inputCls} /></div>
              <div><label for="iv-due" class={labelCls}>Due date</label><input id="iv-due" type="date" bind:value={editing.dueDate} disabled={!editable} min={editing.issueDate} class={inputCls} /></div>
            </div>
            {#if editable}
              <div><label for="iv-terms" class={labelCls}>Payment terms</label>
                <select id="iv-terms" onchange={(e) => { setTerms(e.target.value); e.target.value = ''; }} class={selectCls}>
                  <option value="">Set the due date from the invoice date…</option>
                  <option value="0">Due on receipt</option><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="60">60 days</option>
                </select></div>
            {/if}
            <div class="grid grid-cols-2 gap-3">
              <div><label for="iv-disc-type" class={labelCls}>Discount</label>
                <select id="iv-disc-type" bind:value={editing.discountType} disabled={locked} class={selectCls}><option value="none">None</option><option value="percent">Percent (%)</option><option value="amount">Amount ({currency})</option></select></div>
              <div><label for="iv-disc" class={labelCls}>Value</label><input id="iv-disc" type="number" min="0" step="0.01" inputmode="decimal" bind:value={editing.discountValue} disabled={locked || editing.discountType === 'none'} class="{inputCls} tabular-nums" /></div>
              <div><label for="iv-tax" class={labelCls}>Tax rate (%)</label><input id="iv-tax" type="number" min="0" max="100" step="0.01" inputmode="decimal" bind:value={editing.taxRate} disabled={locked} class="{inputCls} tabular-nums" /></div>
              <div><label for="iv-taxl" class={labelCls}>Tax name</label><input id="iv-taxl" bind:value={editing.taxLabel} disabled={locked} maxlength="20" class={inputCls} /></div>
            </div>
            <dl class="space-y-2 border-t border-border pt-4 text-sm">
              <div class="flex justify-between"><dt class="text-muted-foreground">Subtotal</dt><dd class="tabular-nums">{money(t.subtotal, editing.currency)}</dd></div>
              {#if t.discount > 0}<div class="flex justify-between"><dt class="text-muted-foreground">Discount</dt><dd class="tabular-nums">-{money(t.discount, editing.currency)}</dd></div>{/if}
              {#if Number(editing.taxRate) > 0}<div class="flex justify-between"><dt class="text-muted-foreground">{editing.taxLabel || 'VAT'} ({Number(editing.taxRate)}%)</dt><dd class="tabular-nums">{money(t.tax, editing.currency)}</dd></div>{/if}
              <div class="flex justify-between border-t border-border pt-2 text-base font-semibold"><dt>Total</dt><dd class="tabular-nums">{money(t.total, editing.currency)}</dd></div>
              {#if bal.paid > 0}
                <div class="flex justify-between"><dt class="text-muted-foreground">Paid</dt><dd class="tabular-nums">-{money(bal.paid, editing.currency)}</dd></div>
                <div class="flex justify-between font-semibold"><dt>Balance due</dt><dd class="tabular-nums">{money(bal.balance, editing.currency)}</dd></div>
              {/if}
            </dl>
          </div>
        </Card>

        <Card title="Actions">
          <div class="space-y-3 p-6 pt-4">
            {#if editing.status === 'draft'}
              <Button class="w-full" disabled={!!busy} onclick={issue}>{#if busy === 'issue'}<LoaderCircle size={15} class="animate-spin" />{/if} Issue invoice</Button>
              <Button variant="outline" class="w-full" disabled={!!busy} onclick={doSave}>{#if busy === 'save'}<LoaderCircle size={15} class="animate-spin" />{/if} Save draft</Button>
              <p class={hintCls}>Issuing gives the invoice the next number and locks its amounts. Then download the PDF and send it yourself.</p>
            {:else if editable}
              <Button variant="outline" class="w-full" disabled={!!busy} onclick={doSave}>{#if busy === 'save'}<LoaderCircle size={15} class="animate-spin" />{/if} Save due date and notes</Button>
            {/if}

            <Button variant="outline" class="w-full" disabled={!!busy} onclick={downloadPdf}>{#if busy === 'pdf'}<LoaderCircle size={15} class="animate-spin" />{:else}<Download size={15} />{/if} Download PDF{editing.status === 'draft' ? ' (preview)' : ''}</Button>
            {#if editing.id}<Button variant="outline" class="w-full" disabled={!!busy} onclick={() => duplicate()}><Copy size={15} /> Duplicate</Button>{/if}
            {#if editing.status === 'issued'}<Button variant="ghost" class="w-full text-destructive hover:text-destructive" disabled={!!busy} onclick={voidInvoice}><Ban size={15} /> Void invoice</Button>{/if}
            {#if editing.status === 'draft'}<Button variant="ghost" class="w-full text-destructive hover:text-destructive" disabled={!!busy} onclick={removeDraft}><Trash2 size={15} /> Delete draft</Button>{/if}
          </div>
        </Card>
      </div>
    </div>
  </div>
{/if}
